import React, { useState } from 'react';

import { useDroppable } from '@dnd-kit/core';

import { IconButton } from '@atlaskit/button/new';
import ChevronDownIcon from '@atlaskit/icon/glyph/chevron-down';
import ChevronRightIcon from '@atlaskit/icon/glyph/chevron-right';
import FolderIcon from '@atlaskit/icon/glyph/folder';
import FolderFilledIcon from '@atlaskit/icon/glyph/folder-filled';
import Tooltip from '@atlaskit/tooltip';

import { FEATURE_FLAG } from '@qality/common-config';
import { Folder as FolderModel } from '@qality/common-model';
import { FolderWithParent } from '@qality/common-model/src/Folder';
import { TestCaseRepositoryDndUtils } from '@qality/common-pages/src/TestCaseRepositoryPage/TestCaseRepositoryDnd/utils';
import { useOverflowObserver } from '@qality/common-utils';

import { TreeNavigationButton } from '../TreeNavigationButton';
import { ContainerStyled, ToggleExpandButtonStyled } from './Folder.styled';
import { FolderPath } from './FolderPath';

export type Props = {
  folder: FolderModel | FolderWithParent;
  onClick: (folder: FolderModel) => void | Promise<void>;
  onExpand?: (folder: FolderModel) => void | Promise<void>;
  onCollapse?: (folder: FolderModel) => void | Promise<void>;
  isHighlighted?: (folder: FolderModel) => {
    isFolderHighlighted: boolean;
    isWholeSubtreeHighlighted: boolean;
  };
  isExpandable?: (folder: FolderModel) => boolean;
  isDisabled?: (folder: FolderModel) => boolean;
  isExpanded?: (folder: FolderModel) => boolean;
  getSubfolders?: (folder: FolderModel) => Array<FolderModel>;
  depthLevel?: number;
};

export function Folder({
  folder,
  onClick,
  isHighlighted = () => ({
    isFolderHighlighted: false,
    isWholeSubtreeHighlighted: false,
  }),
  isExpandable = () => false,
  isDisabled = () => false,
  isExpanded = () => false,
  onExpand = () => undefined,
  onCollapse = () => undefined,
  getSubfolders = () => undefined,
  depthLevel = 0,
}: Props): JSX.Element {
  const [isLoading, setIsLoading] = useState(false);
  const [isTooltipDisabled, setIsTooltipDisabled] = useState(false);

  const { ref, isOverflowing } = useOverflowObserver();

  const { isFolderHighlighted, isWholeSubtreeHighlighted } =
    isHighlighted(folder);

  const isFolderExpanded = isExpanded(folder);

  const hasSubfolders = folder.totalSubfolders > 0;
  const hasTestCases = folder.totalTestCases > 0;
  const isEmpty = !hasSubfolders && !hasTestCases;

  const isToggleExpandButtonVisible = hasSubfolders && isExpandable(folder);

  const isDroppableDisabled =
    !FEATURE_FLAG.isPremium || isDisabled(folder) || !folder.canEdit;

  const { setNodeRef, active, isOver } = useDroppable({
    id: TestCaseRepositoryDndUtils.composeId({ item: 'folder', id: folder.id }),
    data: TestCaseRepositoryDndUtils.composeData({
      type: 'folder',
      payload: { folder, isDisabled: isDroppableDisabled },
    }),
  });

  const handleFolderExpand = async (
    event: React.MouseEvent<HTMLButtonElement>
  ) => {
    event.stopPropagation();

    if (isFolderExpanded) {
      return;
    }

    setIsLoading(true);
    await onExpand(folder);
    setIsLoading(false);
  };

  const handleFolderCollapse = async (
    event: React.MouseEvent<HTMLButtonElement>
  ) => {
    event.stopPropagation();

    if (!isFolderExpanded) {
      return;
    }

    setIsLoading(true);
    await onCollapse(folder);
    setIsLoading(false);
  };

  const handleFolderClick = async () => {
    setIsLoading(true);
    await onClick(folder);
    setIsLoading(false);
  };

  const icon = isEmpty ? (
    <FolderIcon label='Empty folder' size='small' />
  ) : (
    <FolderFilledIcon label='Folder with items' size='small' />
  );

  const tooltipControls = {
    onMouseOver: () => setIsTooltipDisabled(true),
    onMouseLeave: () => setIsTooltipDisabled(false),
  };

  const actions =
    isToggleExpandButtonVisible &&
    (isFolderExpanded ? (
      <ToggleExpandButtonStyled>
        <IconButton
          appearance='subtle'
          isLoading={isLoading}
          icon={ChevronDownIcon}
          label='Collapse folder'
          onClick={handleFolderCollapse}
          {...tooltipControls}
        />
      </ToggleExpandButtonStyled>
    ) : (
      <ToggleExpandButtonStyled>
        <IconButton
          appearance='subtle'
          isLoading={isLoading}
          icon={ChevronRightIcon}
          label='Expand folder'
          onClick={handleFolderExpand}
          {...tooltipControls}
        />
      </ToggleExpandButtonStyled>
    ));

  const subfolders =
    isFolderExpanded &&
    getSubfolders?.(folder).map((subfolder) => (
      <Folder
        key={subfolder.id}
        folder={subfolder}
        onClick={onClick}
        onExpand={onExpand}
        onCollapse={onCollapse}
        isHighlighted={isHighlighted}
        isExpandable={isExpandable}
        isDisabled={() => isDroppableDisabled}
        isExpanded={isExpanded}
        getSubfolders={getSubfolders}
        depthLevel={depthLevel + 1}
      />
    ));

  return (
    <ContainerStyled
      isHighlighted={isWholeSubtreeHighlighted}
      depthLevel={depthLevel}
    >
      <Tooltip
        content={!isTooltipDisabled && isOverflowing && folder.name}
        position='bottom-start'
      >
        <TreeNavigationButton
          isHighlighted={isFolderHighlighted}
          isDraggingOver={!isDroppableDisabled && isOver}
          isDisabled={isDroppableDisabled && !!active}
          onClick={handleFolderClick}
          innerRef={setNodeRef}
          iconBefore={icon}
          actions={actions}
          data-testid='folder'
        >
          <FolderPath
            folder={folder}
            showOnlyFolderName={isExpandable(folder)}
            innerRef={ref}
          />
        </TreeNavigationButton>
      </Tooltip>

      {subfolders}
    </ContainerStyled>
  );
}
